// client/src/components/admin/AdminDashboard.tsx

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { getAdminDashboardData, getSchools, createSchool, School, updateSchoolPaymentStatus, getSchoolPaymentStatus } from "../../services/adminService";
import { FiPlus, FiRefreshCw, FiAlertCircle, FiEye } from "react-icons/fi";
import { toast } from 'react-toastify';
import { api } from "../../services/api";
import PaymentManagement from './PaymentManagement';
import {
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel
} from '@chakra-ui/tabs';
import {
  Box,
  Badge,
  Input,
  Text,
  Select,
  useDisclosure
} from '@chakra-ui/react';
import { FormControl, FormLabel } from '@chakra-ui/form-control';

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  font-family: 'Inter', sans-serif;
`;

const Header = styled.div`
  background-color: ${({ theme }) => theme.primary || "#7e22ce"};
  color: white;
  padding: 2rem;
  border-radius: 1rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const MetricCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const MetricValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  margin-bottom: 0.5rem;
`;

const MetricLabel = styled.div`
  color: #64748b;
  font-size: 0.9rem;
`;

const Section = styled.section`
  background: white;
  padding: 2rem;
  border-radius: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: #64748b;
`;

const StyledInput = styled.input`
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary || "#7e22ce"};
  }
`;

const StyledButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${({ theme }) => theme.primary || "#7e22ce"};
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.primaryDark || "#6b21a8"};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SchoolsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const SchoolCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const SchoolName = styled.h3`
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  font-size: 1.2rem;
  margin-bottom: 1rem;
`;

const SchoolInfo = styled.p`
  color: #64748b;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TeacherForm = styled.form`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
`;

const TeacherList = styled.div`
  margin-top: 1rem;
`;

const TeacherItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: #f8fafc;
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
`;

const TeacherInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const TeacherName = styled.span`
  font-weight: 500;
`;

const TeacherId = styled.small`
  color: #64748b;
  font-size: 0.8rem;
`;

const TeacherEmail = styled.small`
  color: #64748b;
  font-size: 0.8rem;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-weight: 500;
  background-color: ${({ status }) => 
    status === 'active' ? '#dcfce7' : 
    status === 'pending' ? '#fef9c3' : 
    status === 'paid' ? '#dbeafe' :
    '#fee2e2'};
  color: ${({ status }) => 
    status === 'active' ? '#166534' : 
    status === 'pending' ? '#854d0e' : 
    status === 'paid' ? '#1e40af' :
    '#991b1b'};
`;

const Message = styled.div<{ type: 'success' | 'error' }>`
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${({ type }) => type === 'success' ? '#dcfce7' : '#fee2e2'};
  color: ${({ type }) => type === 'success' ? '#166534' : '#991b1b'};
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid ${({ theme }) => theme.primary || "#7e22ce"};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SchoolsSummary = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SchoolsCount = styled.div`
  font-size: 1.2rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RefreshButton = styled(StyledButton)`
  background-color: #3b82f6;
  &:hover {
    background-color: #2563eb;
  }
`;

const ViewButton = styled(StyledButton)`
  background-color: ${({ theme }) => theme.primaryLight || "#c4b5fd"};
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  
  &:hover {
    background-color: ${({ theme }) => theme.primary || "#7e22ce"};
    color: white;
  }
`;

export interface AdminDashboardData {
  totalApplications: number;
  totalUsers: number;
  systemUptime: string;
  activeSessions: number;
}

const AdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<AdminDashboardData | null>(null);
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showSchools, setShowSchools] = useState<boolean>(true);
  const [selectedSchool, setSelectedSchool] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<School['mpesaPayment'] | null>(null);
  const [newSchool, setNewSchool] = useState({
    name: '',
    location: '',
    contactEmail: '',
    contactPhone: '',
    schoolId: '',
    postalCode: '',
    country: '',
    state: '',
    city: '',
    address: ''
  });
  const [teacherForms, setTeacherForms] = useState<Map<string, {
    firstName: string;
    lastName: string;
    email: string;
    schoolId: string;
  }>>(new Map());
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [tabs, setTabs] = useState<string[]>(['dashboard', 'payments']);

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchDashboardData(),
          fetchSchools()
        ]);
      } catch (err: any) {
        setError(err.message || "Failed to initialize dashboard data.");
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const data = await getAdminDashboardData();
      setDashboardData(data);
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || "Failed to fetch dashboard data.");
    }
  };

  const fetchSchools = async () => {
    setLoading(true);
    setError(null); // Clear any previous errors
    try {
      console.log('Fetching schools...');
      const schoolsData = await getSchools();
      console.log('Raw schools data:', schoolsData);
      
      if (!Array.isArray(schoolsData)) {
        console.error('Invalid schools data format:', schoolsData);
        throw new Error('Invalid response format from server');
      }
      
      setSchools(schoolsData);
      console.log('Schools state updated:', schoolsData.length, 'schools');
      console.log('First school data:', schoolsData[0]);
    } catch (err: any) {
      console.error('Error in fetchSchools:', err);
      setError(err.message || "Failed to fetch schools");
      setSchools([]); // Reset schools to empty array on error
      toast.error(err.message || "Failed to fetch schools");
    } finally {
      setLoading(false);
    }
  };

  // Add a debug effect to monitor schools state
  useEffect(() => {
    console.log('Schools state updated:', schools);
  }, [schools]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewSchool(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTeacherInputChange = (schoolId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTeacherForms(prev => {
      const newMap = new Map(prev);
      const currentForm = newMap.get(schoolId) || {
        firstName: '',
        lastName: '',
        email: '',
        schoolId
      };
      newMap.set(schoolId, {
        ...currentForm,
        [name]: value
      });
      return newMap;
    });
  };

  const handleAddTeacher = async (schoolId: string, e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const teacherData = teacherForms.get(schoolId);
      if (!teacherData) {
        throw new Error('Teacher data not found');
      }

      const response = await api.addTeacher(schoolId, {
        ...teacherData,
        schoolId
      });

      // Update schools state with new teacher
      setSchools(prev => prev.map(school => 
        school._id === schoolId 
          ? { ...school, teachers: [...(school.teachers || []), response.data] }
          : school
      ));

      // Show success message with temporary password
      toast.success(
        <div>
          <p>Teacher added successfully!</p>
          <p>Temporary password: <strong>{response.data.tempPassword}</strong></p>
          <p>Please share this with the teacher.</p>
        </div>
      );

      // Reset form for this specific school
      setTeacherForms(prev => {
        const newMap = new Map(prev);
        newMap.set(schoolId, {
          firstName: '',
          lastName: '',
          email: '',
          schoolId
        });
        return newMap;
      });
    } catch (error) {
      console.error('Error adding teacher:', error);
      toast.error('Failed to add teacher');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Construct location from address details
      const location = `${newSchool.address}, ${newSchool.city}, ${newSchool.state}, ${newSchool.country} ${newSchool.postalCode}`;
      const schoolData = {
        ...newSchool,
        location
      };

      const school = await createSchool(schoolData);
      setSchools(prev => Array.isArray(prev) ? [...prev, school] : [school]);
      toast.success('School added successfully!');
      
      // Reset form
      setNewSchool({
        name: '',
        location: '',
        contactEmail: '',
        contactPhone: '',
        schoolId: '',
        postalCode: '',
        country: '',
        state: '',
        city: '',
        address: ''
      });
    } catch (error) {
      console.error('Error adding school:', error);
      toast.error('Failed to add school');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentStatusUpdate = async (schoolId: string, status: 'pending' | 'completed' | 'failed') => {
    setLoading(true);
    try {
      const school = await updateSchoolPaymentStatus(schoolId, {
        transactionId: `MPESA-${Date.now()}`,
        amount: 1000, // Example amount, should be configurable
        status
      });

      // Update schools state with new payment status
      setSchools(prev => prev.map(s => 
        s._id === schoolId 
          ? { ...s, status: status === 'completed' ? 'paid' : 'pending', mpesaPayment: school.mpesaPayment }
          : s
      ));

      toast.success(`Payment status updated to ${status}`);
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Failed to update payment status');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPaymentStatus = async (schoolId: string) => {
    setLoading(true);
    try {
      const status = await getSchoolPaymentStatus(schoolId);
      setPaymentStatus(status);
      setSelectedSchool(schoolId);
    } catch (error) {
      console.error('Error fetching payment status:', error);
      toast.error('Failed to fetch payment status');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <>
            {dashboardData && (
              <DashboardGrid>
                <MetricCard>
                  <MetricValue>{dashboardData.totalApplications}</MetricValue>
                  <MetricLabel>Total Applications</MetricLabel>
                </MetricCard>
                <MetricCard>
                  <MetricValue>{dashboardData.totalUsers}</MetricValue>
                  <MetricLabel>Total Users</MetricLabel>
                </MetricCard>
                <MetricCard>
                  <MetricValue>{dashboardData.systemUptime}</MetricValue>
                  <MetricLabel>System Uptime</MetricLabel>
                </MetricCard>
                <MetricCard>
                  <MetricValue>{dashboardData.activeSessions}</MetricValue>
                  <MetricLabel>Active Sessions</MetricLabel>
                </MetricCard>
              </DashboardGrid>
            )}

            <Section>
              <SectionTitle>
                <FiPlus /> Add New School
              </SectionTitle>
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label>School Name</Label>
                  <StyledInput
                    type="text"
                    name="name"
                    value={newSchool.name}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>School ID</Label>
                  <StyledInput
                    type="text"
                    name="schoolId"
                    value={newSchool.schoolId}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Contact Email</Label>
                  <StyledInput
                    type="email"
                    name="contactEmail"
                    value={newSchool.contactEmail}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Contact Phone</Label>
                  <StyledInput
                    type="tel"
                    name="contactPhone"
                    value={newSchool.contactPhone}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Address</Label>
                  <StyledInput
                    type="text"
                    name="address"
                    value={newSchool.address}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>City</Label>
                  <StyledInput
                    type="text"
                    name="city"
                    value={newSchool.city}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>State</Label>
                  <StyledInput
                    type="text"
                    name="state"
                    value={newSchool.state}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Country</Label>
                  <StyledInput
                    type="text"
                    name="country"
                    value={newSchool.country}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Postal Code</Label>
                  <StyledInput
                    type="text"
                    name="postalCode"
                    value={newSchool.postalCode}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <StyledButton type="submit" disabled={loading}>
                  <FiPlus /> Add School
                </StyledButton>
              </Form>
            </Section>

            {error && (
              <Message type="error">
                <FiAlertCircle /> {error}
              </Message>
            )}

            <Section>
              <SectionTitle>
                <FiRefreshCw /> Schools Management
              </SectionTitle>
              
              <SchoolsSummary>
                <SchoolsCount>
                  {loading ? (
                    <Spinner style={{ width: '20px', height: '20px' }} />
                  ) : (
                    <>
                      {schools.length} {schools.length === 1 ? 'School' : 'Schools'} Registered
                    </>
                  )}
                </SchoolsCount>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <RefreshButton onClick={fetchSchools} disabled={loading}>
                    <FiRefreshCw /> Refresh
                  </RefreshButton>
                  <ViewButton onClick={() => setShowSchools(!showSchools)}>
                    <FiEye /> {showSchools ? 'Hide Schools' : 'View Schools'}
                  </ViewButton>
                </div>
              </SchoolsSummary>
              
              {showSchools && (
                <>
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <Spinner />
                      <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading schools...</p>
                    </div>
                  ) : schools.length > 0 ? (
                    <SchoolsGrid>
                      {schools.map((school) => (
                        <SchoolCard key={school._id}>
                          <SchoolName>{school.name}</SchoolName>
                          <SchoolInfo>
                            <strong>School ID:</strong> {school.schoolId}
                          </SchoolInfo>
                          <SchoolInfo>
                            <strong>Location:</strong> {school.location}
                          </SchoolInfo>
                          <SchoolInfo>
                            <strong>Contact Email:</strong> {school.contactEmail}
                          </SchoolInfo>
                          <SchoolInfo>
                            <strong>Contact Phone:</strong> {school.contactPhone}
                          </SchoolInfo>
                          <SchoolInfo>
                            <strong>Status:</strong>{" "}
                            <StatusBadge status={school.status || 'pending'}>
                              {school.status || 'pending'}
                            </StatusBadge>
                          </SchoolInfo>

                          {/* Payment Status Section */}
                          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                            <SchoolInfo>
                              <strong>Payment Status:</strong>
                              {school.mpesaPayment ? (
                                <StatusBadge status={school.mpesaPayment.status}>
                                  {school.mpesaPayment.status}
                                </StatusBadge>
                              ) : (
                                <span>No payment record</span>
                              )}
                            </SchoolInfo>
                            
                            {selectedSchool === school._id && paymentStatus && (
                              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                                <SchoolInfo>
                                  <strong>Transaction ID:</strong> {paymentStatus.transactionId}
                                </SchoolInfo>
                                <SchoolInfo>
                                  <strong>Amount:</strong> KES {paymentStatus.amount}
                                </SchoolInfo>
                                <SchoolInfo>
                                  <strong>Paid At:</strong> {new Date(paymentStatus.paidAt).toLocaleString()}
                                </SchoolInfo>
                              </div>
                            )}

                            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                              <StyledButton 
                                onClick={() => handleViewPaymentStatus(school._id)}
                                style={{ backgroundColor: '#3b82f6' }}
                              >
                                <FiEye /> View Payment
                              </StyledButton>
                              <StyledButton 
                                onClick={() => handlePaymentStatusUpdate(school._id, 'completed')}
                                style={{ backgroundColor: '#22c55e' }}
                              >
                                Mark as Paid
                              </StyledButton>
                            </div>
                          </div>
                          
                          {/* Teacher Management Section */}
                          <TeacherForm onSubmit={(e) => handleAddTeacher(school._id, e)}>
                            <FormGroup>
                              <Label>Add Teacher</Label>
                              <StyledInput
                                type="text"
                                name="firstName"
                                placeholder="First Name"
                                value={teacherForms.get(school._id)?.firstName || ''}
                                onChange={(e) => handleTeacherInputChange(school._id, e)}
                                required
                              />
                              <StyledInput
                                type="text"
                                name="lastName"
                                placeholder="Last Name"
                                value={teacherForms.get(school._id)?.lastName || ''}
                                onChange={(e) => handleTeacherInputChange(school._id, e)}
                                required
                              />
                              <StyledInput
                                type="email"
                                name="email"
                                placeholder="Email"
                                value={teacherForms.get(school._id)?.email || ''}
                                onChange={(e) => handleTeacherInputChange(school._id, e)}
                                required
                              />
                              <StyledButton type="submit" disabled={loading}>
                                <FiPlus /> Add Teacher
                              </StyledButton>
                            </FormGroup>
                          </TeacherForm>

                          {/* Teachers List */}
                          <TeacherList>
                            <SchoolInfo>
                              <strong>Teachers:</strong> {school.teachers?.length || 0}
                            </SchoolInfo>
                            {school.teachers?.map((teacher: any) => (
                              <TeacherItem key={teacher._id}>
                                <TeacherInfo>
                                  <TeacherName>
                                    {teacher.firstName} {teacher.lastName}
                                  </TeacherName>
                                  <TeacherId>ID: {teacher.teacherId}</TeacherId>
                                  <TeacherEmail>{teacher.email}</TeacherEmail>
                                </TeacherInfo>
                                <StatusBadge status={teacher.status}>
                                  {teacher.status}
                                </StatusBadge>
                              </TeacherItem>
                            ))}
                          </TeacherList>
                        </SchoolCard>
                      ))}
                    </SchoolsGrid>
                  ) : (
                    <Message type="error">
                      <FiAlertCircle /> No schools registered yet.
                    </Message>
                  )}
                </>
              )}
            </Section>
          </>
        );
      case 'payments':
        return <PaymentManagement />;
      default:
        return <Text>Select a tab to view content</Text>;
    }
  };

  if (loading) {
    return (
      <LoadingOverlay>
        <Spinner />
      </LoadingOverlay>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Admin Dashboard</Title>
        <Subtitle>Manage schools and monitor system metrics</Subtitle>
      </Header>

      <Tabs onChange={(index) => setActiveTab(tabs[index])}>
        <TabList>
          {tabs.map((tab) => (
            <Tab key={tab}>{tab}</Tab>
          ))}
        </TabList>
        <TabPanels>
          {tabs.map((tab) => (
            <TabPanel key={tab}>
              {renderContent()}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Container>
  );
};

export default AdminDashboard;
